import React, { ReactNode, useEffect, useState } from 'react';
import { useTenant, TenantTheme } from '../contexts/TenantContext';

export interface ThemeProviderProps {
  children: ReactNode;
  tenantKey?: string;
  fallbackTheme?: TenantTheme;
}

/**
 * Enhanced ThemeProvider component that loads tenant-specific themes
 * and provides theme utilities to child components
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  tenantKey,
  fallbackTheme 
}) => {
  const { theme, setTenant, isLoading, error } = useTenant();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize theme when tenantKey is provided
  useEffect(() => {
    const initializeTheme = async () => {
      if (tenantKey && !isInitialized) {
        try {
          await setTenant(tenantKey, fallbackTheme);
          setIsInitialized(true);
        } catch (error) {
          console.error('Failed to initialize theme:', error);
          setIsInitialized(true); // Still mark as initialized to prevent infinite loops
        }
      } else if (!tenantKey) {
        setIsInitialized(true);
      }
    };

    initializeTheme();
  }, [tenantKey, fallbackTheme, setTenant, isInitialized]);

  // Show loading state while theme is being loaded
  if (isLoading && !isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando tema...</p>
        </div>
      </div>
    );
  }

  // Show error state if theme loading failed
  if (error && !theme) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error al cargar el tema
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-transition">
      {children}
    </div>
  );
};

/**
 * Hook to access theme utilities
 */
export const useTheme = () => {
  const { theme, displayName, tenantKey } = useTenant();

  const getLogoUrl = () => theme?.branding.logoUrl || '/images/logo.png';
  const getFaviconUrl = () => theme?.branding.faviconUrl || '/favicon.svg';
  const getPrimaryColor = () => theme?.branding.colors.primary || '#3B82F6';
  const getSecondaryColor = () => theme?.branding.colors.secondary || '#10B981';
  const getAccentColor = () => theme?.branding.colors.accent || '#F59E0B';
  const getDisplayName = () => displayName || 'Colegio Sin Notas';
  const getWelcomeMessage = () => theme?.customization.welcomeMessage || 'Bienvenido al Sistema de Evaluación Integral';
  const getFooterText = () => theme?.customization.footerText || 'Sistema de Evaluación Integral';
  const getSupportEmail = () => theme?.customization.supportEmail || 'soporte@colegio.cl';
  const getSupportPhone = () => theme?.customization.supportPhone || '+56 9 1234 5678';

  const isCPV = () => tenantKey === 'cpv.cl';

  return {
    theme,
    displayName,
    tenantKey,
    getLogoUrl,
    getFaviconUrl,
    getPrimaryColor,
    getSecondaryColor,
    getAccentColor,
    getDisplayName,
    getWelcomeMessage,
    getFooterText,
    getSupportEmail,
    getSupportPhone,
    isCPV
  };
};

export default ThemeProvider;